import { GAME_WIDTH, LEVELS } from './gameConfig';

const DEFAULT_LEVEL_INDEX = 0;

function expandWave(wave, level) {
  const startMs = wave.startMs ?? 0;
  const intervalMs = wave.intervalMs ?? level.spawnRateMs ?? 800;

  if (wave.pattern === 'formationEntry') {
    const lanes = wave.lanes ?? [72, 126, 180, 234, 288];
    const rows = wave.rows ?? 1;

    return Array.from({ length: rows }).flatMap((_, rowIndex) =>
      lanes.map((x, laneIndex) => ({
        atMs: startMs + (rowIndex * lanes.length + laneIndex) * intervalMs,
        type: wave.enemyTypes?.[laneIndex % wave.enemyTypes.length] ?? level.enemyTypes?.[0],
        x,
        y: (wave.y ?? -36) - rowIndex * 18,
        baseX: x,
        movement: rowIndex % 2 === 0 ? 'straight' : 'drift',
        drift: laneIndex < lanes.length / 2 ? 1 : -1,
        phase: rowIndex * 0.6 + laneIndex * 0.2,
      }))
    );
  }

  if (wave.pattern === 'crossfirePair') {
    const pairs = wave.pairs ?? 1;
    const leftX = wave.leftX ?? 52;
    const rightX = wave.rightX ?? GAME_WIDTH - 52;
    const enemyTypes = wave.enemyTypes?.length ? wave.enemyTypes : level.enemyTypes;

    return Array.from({ length: pairs }).flatMap((_, pairIndex) => {
      const atMs = startMs + pairIndex * intervalMs;
      const type = enemyTypes?.[pairIndex % enemyTypes.length] ?? 'raider';

      return [
        {
          atMs,
          type,
          x: leftX,
          y: wave.y ?? -34,
          baseX: leftX + 28,
          movement: 'drift',
          drift: 1,
          phase: pairIndex * 0.5,
        },
        {
          atMs,
          type,
          x: rightX,
          y: wave.y ?? -34,
          baseX: rightX - 28,
          movement: 'drift',
          drift: -1,
          phase: pairIndex * 0.5 + Math.PI,
        },
      ];
    });
  }

  if (wave.pattern === 'powerupEscort') {
    const lanes = wave.lanes ?? [88, 134, 226, 272];
    const repeats = wave.repeats ?? 1;
    const delayBetweenRepeatsMs = wave.delayBetweenRepeatsMs ?? lanes.length * intervalMs;
    const escortX = wave.escortX ?? GAME_WIDTH / 2;

    return Array.from({ length: repeats }).flatMap((_, repeatIndex) => {
      const waveStart = startMs + repeatIndex * delayBetweenRepeatsMs;
      const wingmen = lanes.map((x, laneIndex) => ({
        atMs: waveStart + (laneIndex + 1) * intervalMs,
        type: laneIndex % 2 === 0 ? 'scout' : 'raider',
        x,
        y: -34,
        baseX: x,
        movement: laneIndex % 2 === 0 ? 'straight' : 'sine',
        drift: x < escortX ? 1 : -1,
        phase: repeatIndex + laneIndex * 0.55,
      }));

      return [
        {
          atMs: waveStart,
          type: 'heavy',
          x: escortX,
          y: -50,
          baseX: escortX,
          movement: 'drift',
          drift: repeatIndex % 2 === 0 ? -1 : 1,
        },
        ...wingmen,
        {
          atMs: waveStart + (lanes.length + 1) * intervalMs,
          type: 'heavy',
          x: repeatIndex % 2 === 0 ? 118 : 242,
          y: -50,
          baseX: repeatIndex % 2 === 0 ? 118 : 242,
          movement: 'sine',
          phase: repeatIndex * Math.PI,
        },
      ];
    });
  }

  return [];
}

export function getWaveSchedule(level = LEVELS[DEFAULT_LEVEL_INDEX]) {
  if (!level.waves?.length) {
    return [];
  }

  return level.waves
    .flatMap((wave) => expandWave(wave, level))
    .sort((a, b) => a.atMs - b.atMs);
}

export function getHazardSchedule(level = LEVELS[DEFAULT_LEVEL_INDEX]) {
  if (!level.hazards?.length) {
    return [];
  }

  return level.hazards
    .flatMap((hazardConfig) => {
      const intervalMs = hazardConfig.intervalMs ?? 1500;
      const startMs = hazardConfig.startMs ?? 0;
      const durationMs = level.durationMs ?? 22000;
      const count = hazardConfig.count ?? Math.ceil((durationMs - startMs) / intervalMs);
      const lanes = hazardConfig.lanes ?? [GAME_WIDTH / 2];

      return Array.from({ length: count }).map((_, index) => {
        const x = lanes[index % lanes.length];

        return {
          atMs: startMs + index * intervalMs,
          type: hazardConfig.type,
          x,
          y: hazardConfig.type === 'shieldGate' ? 360 : -30,
          durationMs: hazardConfig.durationMs ?? 1400,
        };
      });
    })
    .sort((a, b) => a.atMs - b.atMs);
}
